import {
  annuityFactor,
  levelPayment,
  roundTo,
} from "@/lib/tool-kit/finance/tvm";

/**
 * Amortization of a fixed-rate loan.
 *
 * Convention: nominal annual rate, compounded at the payment frequency (rate per period
 * = rate ÷ payments per year), payments at the end of each period. Like a lender's
 * schedule, the payment and each period's interest are rounded to the currency's minor
 * unit, so the last payment is whatever clears the remaining balance — it can differ from
 * the regular payment by a few units of the minor currency.
 */

export interface LoanInput {
  principal: number;
  /** Nominal annual rate as a decimal. */
  annualRate: number;
  paymentsPerYear: number;
  /** Scheduled number of payments. */
  periods: number;
  /** Extra paid with every regular payment. */
  extra: number;
  /** Decimal places of the currency's minor unit. */
  digits: number;
  /** Use this regular payment instead of the one the formula gives. */
  payment?: number;
}

export interface LoanRow {
  n: number;
  /** Everything paid this period, extra included. */
  paid: number;
  interest: number;
  principal: number;
  extra: number;
  balance: number;
}

export interface LoanSchedule {
  /** The regular payment (without extra). */
  payment: number;
  rows: LoanRow[];
  totalInterest: number;
  totalPaid: number;
  /** The amount paid in the last period. */
  finalPayment: number;
  /** The payment does not cover the interest, so the balance never falls. */
  neverPaysOff: boolean;
}

const MAX_ROWS = 20_000;

export function regularPayment(input: LoanInput): number {
  if (input.payment !== undefined) {
    return input.payment;
  }
  const i = input.annualRate / input.paymentsPerYear;
  return roundTo(levelPayment(input.principal, i, input.periods), input.digits);
}

/** The loan a payment can service: PMT × [1 − (1+i)^−n] ÷ i. */
export function loanFromPayment(
  payment: number,
  annualRate: number,
  paymentsPerYear: number,
  periods: number
): number {
  return payment * annuityFactor(annualRate / paymentsPerYear, periods);
}

export function buildSchedule(input: LoanInput): LoanSchedule {
  const { principal, paymentsPerYear, periods, digits } = input;
  const i = input.annualRate / paymentsPerYear;
  const payment = regularPayment(input);
  const scheduled = payment + input.extra;
  const rows: LoanRow[] = [];
  let balance = principal;
  let totalInterest = 0;
  let totalPaid = 0;
  for (let n = 1; n <= MAX_ROWS && balance > 0; n += 1) {
    const interest = roundTo(balance * i, digits);
    if (n === 1 && scheduled <= interest && periods > 1) {
      return {
        payment,
        rows,
        totalInterest: 0,
        totalPaid: 0,
        finalPayment: 0,
        neverPaysOff: true,
      };
    }
    const due = roundTo(balance + interest, digits);
    const paid = n >= periods ? due : Math.min(scheduled, due);
    const regular = Math.min(payment, paid);
    balance = roundTo(due - paid, digits);
    totalInterest += interest;
    totalPaid += paid;
    rows.push({
      n,
      paid,
      interest,
      principal: roundTo(paid - interest, digits),
      extra: input.extra > 0 ? roundTo(paid - regular, digits) : 0,
      balance,
    });
  }
  return {
    payment,
    rows,
    totalInterest: roundTo(totalInterest, digits),
    totalPaid: roundTo(totalPaid, digits),
    finalPayment: rows.at(-1)?.paid ?? 0,
    neverPaysOff: false,
  };
}

export interface YearRow {
  year: number;
  paid: number;
  interest: number;
  principal: number;
  balance: number;
}

/** Roll the schedule up by loan year (payments 1…k are year 1, and so on). */
export function yearlySummary(
  rows: readonly LoanRow[],
  paymentsPerYear: number
): YearRow[] {
  const years: YearRow[] = [];
  for (const row of rows) {
    const year = Math.ceil(row.n / paymentsPerYear);
    let entry = years.at(-1);
    if (!entry || entry.year !== year) {
      entry = { year, paid: 0, interest: 0, principal: 0, balance: 0 };
      years.push(entry);
    }
    entry.paid += row.paid;
    entry.interest += row.interest;
    entry.principal += row.principal;
    entry.balance = row.balance;
  }
  return years;
}
